import React from 'react';
import { useTranslation } from '@shared/core';
import { CircleColoredIcon } from '../../components/CircleColoredIcon';
import { FilterList } from '../../components/FilterBase';
import { AnimationType, PhotoCount, ThemeVariant } from './Template.types';
import { getTemplatePhotoCount, getTemplateAnimationType } from './Template.util';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

export const TEMPLATE_DEFAULT_VENDOR = 'Joy';
export const TEMPLATE_COLUMN_COUNT_MOBILE = 2;
export const TEMPLATE_COLUMN_COUNT_DESKTOP = 4;
export const TEMPLATE_GUTTER_DESKTOP = 40;
export const TEMPLATE_GUTTER_MOBILE = 24;
export const TEMPLATE_ASPECT_RATIO = 1.242;
export interface TemplateFilterGroups extends FilterList {
  getValuesFromTemplate: (template: ThemeVariant) => string[];
}

export const useInitialFilterItems = (): TemplateFilterGroups[] => {
  const { tq } = useTranslation('websiteDesigner');

  const [isWebsiteVideoBackgroundHeaderEnabled] = useIsFeatureEnabled('websiteVideoBackgroundHeaderEnabled');

  return [
    {
      label: tq`Layout`,
      getValuesFromTemplate: template => [template.variationType],
      items: [
        { label: tq`Single Page`, value: ['aloha'] },
        { label: tq`Multi-Page`, value: ['brannan'] },
      ],
    },
    {
      label: tq`Categories`,
      getValuesFromTemplate: template => template.tags,
      items: [
        { label: tq`Bold`, value: ['Bold', 'Geometric', 'Bright', 'Typographic'] },
        { label: tq`Classic`, value: ['Formal', 'Elegant', 'Romantic', 'Classic', 'Art Deco'] },
        { label: tq`Cultural`, value: ['Cultural', 'Religious', 'Indian', 'Indonesian', 'Chinese', 'Japanese', 'Mexican'] },
        { label: tq`Destination`, value: ['Destination', 'Beach', 'Mountains', 'Desert', 'Tropical', 'Nautical', 'City'] },
        { label: tq`Floral & Botanical`, value: ['Floral', 'Botanical'] },
        { label: tq`Metallic`, value: ['Metallic'] },
        { label: tq`Modern`, value: ['Modern', 'Geometric', 'Minimal', 'Black & White', 'Typographic'] },
        { label: tq`Rustic`, value: ['Rustic', 'Country', 'Mountains', 'Forest'] },
        { label: tq`Seasonal`, value: ['Seasonal', 'Summer', 'Winter', 'Fall', 'Spring'] },
        { label: tq`Textured`, value: ['Textured', 'Watercolor', 'Paper', 'Marble', 'Paint'] },
      ],
    },
    {
      label: tq`Colors`,
      getValuesFromTemplate: template => [...template.colors, template.variantColor].filter(Boolean) as string[],
      items: [
        { label: tq`Red`, value: ['Red'], icon: <CircleColoredIcon background="#8A1F1F" /> },
        { label: tq`Orange`, value: ['Orange'], icon: <CircleColoredIcon background="#F09E62" /> },
        { label: tq`Yellow`, value: ['Yellow'], icon: <CircleColoredIcon background="#FFE798" /> },
        { label: tq`Green`, value: ['Green'], icon: <CircleColoredIcon background="#627154" /> },
        { label: tq`Blue`, value: ['Blue'], icon: <CircleColoredIcon background="#416497" /> },
        { label: tq`Brown`, value: ['Brown'], icon: <CircleColoredIcon background="#796051" /> },
        { label: tq`Purple`, value: ['Purple'], icon: <CircleColoredIcon background="#866D8E" /> },
        { label: tq`Pink`, value: ['Pink'], icon: <CircleColoredIcon background="#F8DBDA" /> },
        { label: tq`Beige`, value: ['Beige'], icon: <CircleColoredIcon background="#F0E8E2" /> },
        { label: tq`Gray`, value: ['Gray'], icon: <CircleColoredIcon background="#E0E0E0" /> },
        { label: tq`Black`, value: ['Black'], icon: <CircleColoredIcon background="#333333" /> },
        {
          label: tq`White`,
          value: ['White'],
          icon: (
            <CircleColoredIcon
              background="#FFFFFF"
              borderColor="mono5"
            />
          ),
        },
        {
          label: tq`Gold`,
          value: ['Gold'],
          icon: <CircleColoredIcon background="linear-gradient(135deg, #8F5303 0%, #E4C269 28.65%, #FFE68A 42.71%, #F0D16F 55.21%, #BD8A1C 75%, #C59320 84.37%, #AB7613 100%)" />,
        },
        {
          label: tq`Rose Gold`,
          value: ['Rose Gold'],
          icon: (
            <CircleColoredIcon background="linear-gradient(135deg, #89392D 0%, #DF9C7A 28.65%, #FFCBAF 42.71%, #E5A280 55.21%, #A65C47 72.92%, #9B4F3E 84.36%, #87392F 100%)" />
          ),
        },
        {
          label: tq`Silver`,
          value: ['Silver'],
          icon: <CircleColoredIcon background="linear-gradient(135deg, #585550 0%, #908F8B 28.65%, #E5E5E5 42.71%, #BEBDBB 55.21%, #6C6965 75%, #6E6B66 84.37%, #585550 100%)" />,
        },
      ],
    },
    {
      label: tq`Photo`,
      getValuesFromTemplate: template => [getTemplatePhotoCount(template)],
      items: [
        { label: tq`1 Photo`, value: [PhotoCount.One] },
        { label: tq`2+ Photos`, value: [PhotoCount.TwoOrMore] },
        { label: tq`No Photo`, value: [PhotoCount.Zero] },
      ],
    },
    {
      label: tq`Animation`,
      getValuesFromTemplate: template => [getTemplateAnimationType(template)],
      items: [
        ...(isWebsiteVideoBackgroundHeaderEnabled ? [{ label: tq`Video`, value: [AnimationType.Video] }] : []),
        { label: tq`Photo Slider`, value: [AnimationType.PhotoSlider] },
        { label: tq`Photo Marquee`, value: [AnimationType.PhotoMarquee] },
        { label: tq`No Animation`, value: [AnimationType.NoAnimation] },
      ],
    },
    {
      label: tq`Collection`,
      getValuesFromTemplate: template => [template.vendor],
      items: [
        { label: tq`Designed by Joy`, value: ['Joy'] },
        {
          label: tq`Rachel Zoe`,
          value: ['Rachel Zoe'],
          badge: {
            text: tq`New`,
            color: 'white',
            backgroundColor: 'buttonProductiveHover',
          },
        },
        { label: tq`Paper Culture`, value: ['Paper Culture'] },
        { label: tq`Paperlust`, value: ['Paperlust'] },
      ],
    },
  ];
};

export const TEMPLATES_SEARCH_BY = ['variationType', 'vendor', 'tags', 'colors', 'font', 'variantColor', 'variantName', 'variantThemeId', 'theme.name', 'theme.themeId'];

export const UPLOADED_TEMPLATE_PREVIEW_IMAGE_KEY = 'templatePreviewImage';
